<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import WhatsAppCampaignDeliveryReport from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDeliveryReport.vue';
import CampaignRepliesModal from 'dashboard/components-next/Campaigns/CampaignRepliesModal.vue';
import CampaignFailuresModal from 'dashboard/components-next/Campaigns/CampaignFailuresModal.vue';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const router = useRouter();

const selectedCampaign = ref(null);
const editingCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();
const [showDeliveryReport, toggleDeliveryReport] = useToggle();
const [showRepliesModal, toggleRepliesModal] = useToggle();
const [showFailuresModal, toggleFailuresModal] = useToggle();
const repliesFilter = ref('all');

const uiFlags = useMapGetter('whatsappCampaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['whatsappCampaigns/getWhatsappCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const qualityRatingsByInbox = ref({});

const fetchHealthStatuses = async campaigns => {
  const inboxIds = [
    ...new Set(campaigns.map(c => c.inbox?.id).filter(Boolean)),
  ];
  await Promise.all(
    inboxIds.map(async inboxId => {
      try {
        const { data } = await InboxHealthAPI.getHealthStatus(inboxId);
        qualityRatingsByInbox.value[inboxId] = data.quality_rating || '';
      } catch {
        qualityRatingsByInbox.value[inboxId] = '';
      }
    })
  );
};

watch(
  WhatsAppCampaigns,
  campaigns => {
    if (campaigns?.length > 0) {
      fetchHealthStatuses(campaigns);
    }
  },
  { immediate: true }
);

const handleEdit = campaign => {
  editingCampaign.value = campaign;
  toggleWhatsAppCampaignDialog(true);
};

const handleNewCampaign = () => {
  editingCampaign.value = null;
  toggleWhatsAppCampaignDialog(true);
};

const handleCloseDialog = () => {
  editingCampaign.value = null;
  toggleWhatsAppCampaignDialog(false);
};

const handleDelete = campaign => {
  confirmDeleteCampaignDialogRef.value?.open(campaign);
};

const handleViewReport = campaign => {
  selectedCampaign.value = campaign;
  toggleDeliveryReport(true);
};

const handleCloseDeliveryReport = () => {
  toggleDeliveryReport(false);
  selectedCampaign.value = null;
};

const handleCampaignDeleted = () => {
  selectedCampaign.value = null;
};

const handleViewReplies = (campaign, filter = 'all') => {
  selectedCampaign.value = campaign;
  repliesFilter.value = filter;
  toggleRepliesModal(true);
};

const handleCloseRepliesModal = () => {
  toggleRepliesModal(false);
  selectedCampaign.value = null;
};

const handleViewFailures = campaign => {
  selectedCampaign.value = campaign;
  toggleFailuresModal(true);
};

const handleCloseFailuresModal = () => {
  toggleFailuresModal(false);
  selectedCampaign.value = null;
};

const refreshCampaigns = () => store.dispatch('whatsappCampaigns/get');

const handleResume = async campaign => {
  if (
    !confirm(
      `Continuar envios da campanha "${campaign.title}"?\n\nOs contatos que já receberam serão ignorados.`
    )
  ) {
    return;
  }
  try {
    await WhatsappCampaignsAPI.toggle(campaign.id);
    useAlert(
      'Envios retomados com sucesso! A campanha continuará de onde parou.'
    );
    refreshCampaigns();
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao continuar campanha';
    useAlert(msg);
  }
};

const handlePause = async campaign => {
  if (!confirm(`Pausar envios da campanha "${campaign.title}"?`)) {
    return;
  }
  try {
    await WhatsappCampaignsAPI.toggle(campaign.id);
    useAlert('Campanha pausada com sucesso.');
    refreshCampaigns();
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao pausar campanha';
    useAlert(msg);
  }
};

const handleRestart = async campaign => {
  if (
    !confirm(
      `Reiniciar campanha "${campaign.title}" do zero?\n\nTodo o histórico de envios será limpo e a campanha enviará para todos os contatos novamente.`
    )
  ) {
    return;
  }
  try {
    await WhatsappCampaignsAPI.restart(campaign.id);
    useAlert('Campanha reiniciada do zero!');
    refreshCampaigns();
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao reiniciar campanha';
    useAlert(msg);
  }
};

const handleContinueNew = async campaign => {
  if (
    !confirm(
      `Continuar campanha "${campaign.title}" apenas para novos contatos?\n\nOs contatos que já receberam a mensagem serão ignorados. Apenas os novos contatos adicionados à lista receberão o envio.`
    )
  ) {
    return;
  }
  try {
    await WhatsappCampaignsAPI.restart(campaign.id);
    useAlert('Campanha retomada para novos contatos!');
    refreshCampaigns();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      'Erro ao continuar campanha para novos contatos';
    useAlert(msg);
  }
};

const handleRetryFailed = async campaign => {
  const failedCount = campaign.failed_count || 0;
  if (
    !confirm(
      `Reenviar para os ${failedCount} contatos que falharam na campanha "${campaign.title}"?`
    )
  ) {
    return;
  }
  try {
    await WhatsappCampaignsAPI.retryFailed(campaign.id);
    useAlert(`Reenvio iniciado para ${failedCount} contatos falhados!`);
    refreshCampaigns();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      'Erro ao reenviar para contatos falhados';
    useAlert(msg);
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="handleNewCampaign"
    @close="handleCloseDialog"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        :campaign="editingCampaign"
        @close="handleCloseDialog"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      :quality-ratings-by-inbox="qualityRatingsByInbox"
      @edit="handleEdit"
      @delete="handleDelete"
      @view-report="handleViewReport"
      @resume="handleResume"
      @pause="handlePause"
      @restart="handleRestart"
      @continue-new="handleContinueNew"
      @retry-failed="handleRetryFailed"
      @view-failures="handleViewFailures"
      @view-replies="(campaign, filter) => handleViewReplies(campaign, filter)"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      store-module="whatsappCampaigns"
      @deleted="handleCampaignDeleted"
    />
    <CampaignFailuresModal
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      :is-open="showFailuresModal"
      @close="handleCloseFailuresModal"
    />
    <CampaignRepliesModal
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      :is-open="showRepliesModal"
      :filter="repliesFilter"
      campaign-type="whatsapp_campaign"
      @close="handleCloseRepliesModal"
    />
    <WhatsAppCampaignDeliveryReport
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      :is-open="showDeliveryReport"
      campaign-type="whatsapp_campaign"
      @close="handleCloseDeliveryReport"
    />
  </CampaignLayout>
</template>
